import {
  ArrivalCheckFields, Badge, Button, Card, CheckOutDialog, DataTable, DateInput, ErrorState, Field, Input,
  NO_ARRIVAL_READING, Notice, Pager, Permissions, Row, Stack, arrivalBody, atTimeOfDay, shiftIsoDate, timeOfDay, todayIso,
  useApi, useArrivalHealthCheck, useClampedPage, useI18n, usePermission,
  type ArrivalReading, type AttendanceBoardRow,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { LABELS, TONES } from '../attendanceStatus';

/**
 * What the administration does to one row of the board (FR-ATT-06): check a child in, today or on a day already
 * gone with the time they came; check them out through the same collector choice as the staff app (today only);
 * and correct a recorded time with a reason the audit trail keeps. A closed term refuses all three (Q34).
 */
export function AttendanceActions({ row, date }: { row: AttendanceBoardRow; date: string }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t, settings } = useI18n();
  const canRecord = usePermission(Permissions.attendance.record);
  const canCorrect = usePermission(Permissions.attendance.correct);
  const [mode, setMode] = useState<'none' | 'checkIn' | 'checkOut' | 'correct'>('none');
  const isToday = date === todayIso(settings);

  const [checkInTime, setCheckInTime] = useState('');
  const [checkOutTime, setCheckOutTime] = useState('');
  const [reason, setReason] = useState('');

  // The form starts from the row as it is now: the row outlives a change of day or a check-in made since it was drawn.
  const open = (next: 'checkIn' | 'correct') => {
    setCheckInTime(timeOfDay(row.checkInAt, settings));
    setCheckOutTime(timeOfDay(row.checkOutAt, settings));
    setMode(next);
  };

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['attendance'] });
  const at = (time: string) => (time ? atTimeOfDay(date, time, settings) : null);

  // Today's arrival is now unless a time is given; a day gone, or a correction, takes the time typed.
  const timed = !isToday || mode === 'correct';
  const healthCheck = useArrivalHealthCheck();
  const [reading, setReading] = useState<ArrivalReading>(NO_ARRIVAL_READING);
  const [unreadable, setUnreadable] = useState(false);

  const checkIn = useMutation({
    mutationFn: (health: ReturnType<typeof arrivalBody>) => api.attendance.checkIn({
      childId: row.childId, date, at: timed ? at(checkInTime) : null, ...health,
    }),
    onSuccess: async () => { setMode('none'); setReading(NO_ARRIVAL_READING); await refresh(); },
  });

  const saveCheckIn = () => {
    const health = healthCheck ? arrivalBody(reading) : null;
    setUnreadable(healthCheck && health === null);
    if (!healthCheck || health) checkIn.mutate(health);
  };

  const correct = useMutation({
    mutationFn: () => api.attendance.correct(row.attendanceId!, {
      checkInAt: at(checkInTime), checkOutAt: at(checkOutTime), reason: reason.trim(),
    }),
    onSuccess: async () => { setMode('none'); setReason(''); await refresh(); },
  });

  const error = checkIn.error ?? correct.error;

  if (mode === 'checkOut') {
    return <CheckOutDialog row={row} onClose={() => setMode('none')} onDone={() => { setMode('none'); void refresh(); }} />;
  }

  if (mode === 'checkIn' || mode === 'correct') {
    const correcting = mode === 'correct';
    return (
      <Stack tight>
        <Row className="dg-row--wrap">
          {timed && (
            <label className="dg-field">
              <span className="dg-field__label">{t('attendance.checkIn')}</span>
              <Input type="time" value={checkInTime} onChange={(event) => setCheckInTime(event.target.value)} />
            </label>
          )}
          {correcting && (
            <label className="dg-field">
              <span className="dg-field__label">{t('attendance.checkOut')}</span>
              <Input type="time" value={checkOutTime} onChange={(event) => setCheckOutTime(event.target.value)} />
            </label>
          )}
        </Row>
        {correcting && (
          <Input
            aria-label={t('attendance.correctReason')}
            placeholder={t('attendance.correctReason')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        )}
        {!correcting && healthCheck && (
          <ArrivalCheckFields
            value={reading}
            onChange={(next) => { setReading(next); setUnreadable(false); }}
            showInvalid={unreadable}
          />
        )}
        {error && <ErrorState error={error} />}
        <Row>
          <Button
            disabled={(timed && !checkInTime) || (correcting && !reason.trim())}
            busy={checkIn.isPending || correct.isPending}
            onClick={() => (correcting ? correct.mutate() : saveCheckIn())}
          >
            {t('action.save')}
          </Button>
          <Button variant="ghost" onClick={() => setMode('none')}>{t('action.cancel')}</Button>
        </Row>
      </Stack>
    );
  }

  const name = row.preferredName ?? row.firstName;

  return (
    <Stack tight>
      {checkIn.isError && <ErrorState error={checkIn.error} />}
      <Row className="dg-row--wrap">
        {canRecord && !row.checkInAt && (isToday && !healthCheck ? (
          <Button variant="ghost" busy={checkIn.isPending} aria-label={t('attendance.checkInOf', { name })}
            onClick={() => checkIn.mutate(null)}>
            {t('attendance.checkIn')}
          </Button>
        ) : (
          <Button variant="ghost" aria-label={t('attendance.checkInOf', { name })} onClick={() => open('checkIn')}>
            {t('attendance.checkIn')}
          </Button>
        ))}
        {canRecord && isToday && row.status === 'Present' && (
          <Button variant="ghost" aria-label={t('attendance.checkOutOf', { name })} onClick={() => setMode('checkOut')}>
            {t('attendance.checkOut')}
          </Button>
        )}
        {canCorrect && row.attendanceId !== null && (
          <Button variant="ghost" aria-label={t('attendance.correctOf', { name })}
            onClick={() => { correct.reset(); open('correct'); }}>
            {t('attendance.correct')}
          </Button>
        )}
      </Row>
    </Stack>
  );
}

/** The first and last day of the month a `YYYY-MM-DD` date is in. */
function monthOf(date: string): { from: string; to: string } {
  const from = `${date.slice(0, 7)}-01`;
  const nextMonth = shiftIsoDate(from, 32).slice(0, 7);
  return { from, to: shiftIsoDate(`${nextMonth}-01`, -1) };
}

/**
 * A child's attendance a month at a time, with the month's totals (FR-ATT-13), on their page for staff who may read
 * attendance. Staff read from the current academic year (Q33): an earlier month answers with the API's refusal.
 */
export function AttendanceHistoryCard({ childId }: { childId: number }) {
  const api = useApi();
  const { t, formatDate, formatMonth, formatNumber, formatTime, settings } = useI18n();
  const seesAll = usePermission(Permissions.attendance.viewAll);
  const records = usePermission(Permissions.attendance.record);
  const canView = seesAll || records;
  const [month, setMonth] = useState(() => monthOf(todayIso(settings)));

  const history = useQuery({
    queryKey: ['attendance', 'history', childId, month.from],
    queryFn: () => api.attendance.history(childId, month),
    enabled: canView,
  });

  if (!canView) return null;

  const step = (days: number) => setMonth(monthOf(shiftIsoDate(month.from, days)));

  return (
    <Card title={t('attendance.history')}>
      <Stack>
        <Row between>
          <Button variant="ghost" onClick={() => step(-1)}>‹ {t('attendance.previousMonth')}</Button>
          <strong>{formatMonth(month.from)}</strong>
          <Button variant="ghost" onClick={() => step(32)}>{t('attendance.nextMonth')} ›</Button>
        </Row>
        {history.data && (
          <Row className="dg-row--wrap">
            <Badge tone="neutral">{t('attendance.operatingDays', { count: formatNumber(history.data.operatingDays) })}</Badge>
            <Badge tone="success">{t('attendance.present')}: {formatNumber(history.data.present)}</Badge>
            <Badge tone="danger">{t('attendance.absent')}: {formatNumber(history.data.absent)}</Badge>
            <Badge tone="warning">{t('attendance.notified')}: {formatNumber(history.data.notified)}</Badge>
          </Row>
        )}
        <DataTable
          caption={t('attendance.history')}
          rows={[...(history.data?.days ?? [])].reverse()}
          rowKey={(day) => day.date}
          isLoading={history.isPending}
          error={history.error}
          onRetry={() => void history.refetch()}
          empty={t('state.empty')}
          columns={[
            { key: 'date', header: t('audit.when'), render: (day) => formatDate(day.date) },
            { key: 'status', header: t('audit.action'), render: (day) => <Badge tone={TONES[day.status]}>{t(LABELS[day.status])}</Badge> },
            { key: 'in', header: t('attendance.checkIn'), render: (day) => (day.checkInAt ? formatTime(day.checkInAt) : '—') },
            { key: 'out', header: t('attendance.checkOut'), render: (day) => (day.checkOutAt ? formatTime(day.checkOutAt) : '—') },
          ]}
        />
      </Stack>
    </Card>
  );
}

/**
 * The absence notices families sent for a period (FR-ATT-12), today and the next fortnight unless chosen, so the
 * administration sees who will be away before the morning's board does.
 */
export function AbsenceNoticesCard() {
  const api = useApi();
  const { t, formatDate, settings } = useI18n();
  const today = todayIso(settings);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(shiftIsoDate(today, 14));
  const [page, setPage] = useState(1);

  const notices = useQuery({
    queryKey: ['attendance', 'notices', from, to, page],
    queryFn: () => api.attendance.notices({ from, to, page }),
    enabled: from !== '' && to !== '',
  });
  useClampedPage(page, setPage, notices.data?.totalPages);

  return (
    <Card title={t('attendance.notices')}>
      <Stack>
        <Row className="dg-row--wrap">
          <Field label={t('attendance.absenceFrom')}>
            {(props) => <DateInput {...props} value={from} onChange={(value) => { setFrom(value); setPage(1); }} />}
          </Field>
          <Field label={t('attendance.absenceTo')}>
            {(props) => <DateInput {...props} value={to} min={from} onChange={(value) => { setTo(value); setPage(1); }} />}
          </Field>
        </Row>
        {notices.data?.totalCount === 0 && <Notice tone="info">{t('attendance.noNotices')}</Notice>}
        <DataTable
          caption={t('attendance.notices')}
          rows={notices.data?.items}
          rowKey={(notice) => notice.id}
          isLoading={notices.isPending}
          error={notices.error}
          onRetry={() => void notices.refetch()}
          empty={t('attendance.noNotices')}
          columns={[
            { key: 'child', header: t('child.one'), render: (notice) => notice.childName },
            {
              key: 'dates',
              header: t('audit.when'),
              render: (notice) => (notice.dateFrom === notice.dateTo
                ? formatDate(notice.dateFrom)
                : t('calendar.range', { from: formatDate(notice.dateFrom), to: formatDate(notice.dateTo) })),
            },
            { key: 'reason', header: t('attendance.absenceReason'), render: (notice) => notice.reason ?? '—' },
            { key: 'sent', header: t('attendance.noticeSent'), render: (notice) => formatDate(notice.createdAt) },
          ]}
        />
        <Pager page={page} totalPages={notices.data?.totalPages} onPage={setPage} />
      </Stack>
    </Card>
  );
}
